import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import apiService from '../services/api';

interface Company { tenantId: number; name: string; }
interface Mapping { buyerName: string; tenantId: number; }
interface MapsResponse { companies: Company[]; mappings: Mapping[]; unassignedBuyers: string[]; }

const BuyerCompanyMapPanel: React.FC = () => {
  const qc = useQueryClient();
  const [applying, setApplying] = useState(false);
  const { data, isLoading } = useQuery<MapsResponse>({
    queryKey: ['buyer-company-maps'],
    queryFn: () => apiService.get('/buyer-company-maps') as Promise<MapsResponse>,
  });

  const upsert = useMutation({
    mutationFn: (body: { buyerName: string; tenantId: number }) => apiService.put('/buyer-company-maps', body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['buyer-company-maps'] }); toast.success('Saved'); },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Failed to save'),
  });

  const apply = async () => {
    if (!window.confirm('Re-assign all existing yarn records to match these buyer mappings?')) return;
    setApplying(true);
    try {
      const res: any = await apiService.post('/buyer-company-maps/apply', {});
      toast.success(`Moved ${res.total} record(s)`);
      qc.invalidateQueries();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Failed to apply');
    } finally { setApplying(false); }
  };

  if (isLoading || !data) return <div className="p-4 text-sm text-slate-500">Loading buyer mappings…</div>;

  const current = new Map<string, number>(
    data.mappings.map((m: Mapping) => [m.buyerName, m.tenantId] as [string, number])
  );
  const buyers: string[] = [
    ...data.mappings.map((m: Mapping) => m.buyerName),
    ...data.unassignedBuyers,
  ].filter((b: string, i: number, a: string[]) => a.indexOf(b) === i).sort();

  return (
    <div className="bg-white rounded-xl border border-[#E1E6EF] p-4 sm:p-5">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div>
          <h3 className="text-base font-bold text-[#0E131B]">Buyer → Company</h3>
          <p className="text-xs text-slate-500">Each buyer's yarn is owned by the company you map it to.</p>
        </div>
        <button
          onClick={apply}
          disabled={applying}
          className="flex-shrink-0 px-3 py-2 rounded-lg bg-[#0055a9] text-white text-sm font-semibold disabled:opacity-60"
        >
          {applying ? 'Applying…' : 'Apply to existing data'}
        </button>
      </div>
      <div className="space-y-1.5">
        {buyers.length === 0 && <p className="text-sm text-slate-400 py-6 text-center">No buyers found yet.</p>}
        {buyers.map((buyer) => (
          <div key={buyer} className="flex items-center justify-between gap-3 py-2 border-b border-slate-100 last:border-0">
            <span className="text-sm font-medium text-[#0E131B] truncate">{buyer}</span>
            <select
              value={current.get(buyer) ?? ''}
              onChange={(e) => upsert.mutate({ buyerName: buyer, tenantId: Number(e.target.value) })}
              className="flex-shrink-0 w-44 sm:w-56 rounded-lg border border-[#E8EBF1] bg-[#F7F8FB] px-2.5 py-2 text-sm"
            >
              <option value="" disabled>Unassigned</option>
              {data.companies.map((c: Company) => (
                <option key={c.tenantId} value={c.tenantId}>{c.name}</option>
              ))}
            </select>
          </div>
        ))}
      </div>
    </div>
  );
};

export default BuyerCompanyMapPanel;
